import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Grid, TextField } from '@material-ui/core';
import { unifiedGithubApiRef } from '../../../services';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync, useAsyncFn } from 'react-use';
import { Alert, Autocomplete, AutocompleteChangeReason } from '@material-ui/lab';
import { GithubTeamSelect } from '../../../components';
import { GithubOrgPrefix } from '../../../components/GithubOrgPrefix';
import { ADOServiceApiRef } from '../../../services/ado.service';
import { validateGithubRepoName } from '../githubRepo';

export type MigrationRepo = {
  adoRepo: string;
  githubRepo: string;
};

export type ADOProject =
  | {
      name: string;
      id: string;
    }
  | null
  | undefined;

export type ADORepo = {
  name: string;
  id: string;
  url: string;
  project: ADOProject;
};

export type MigrateAdoToGithubInformationFormData = {
  repos?: MigrationRepo[];
  githubTeam?: string | null;
  adoProject?: ADOProject | null;
};

export const MigrateAdoToGithubInformation = ({
  onChange,
  formData,
}: FieldExtensionComponentProps<MigrateAdoToGithubInformationFormData>) => {
  const [adoProject, setAdoProject] = useState<ADOProject | undefined>(formData?.adoProject);
  const [repos, setRepos] = useState<MigrationRepo[] | undefined>(formData?.repos);
  const [organization, setOrganization] = useState('AAInternal');
  const lookupRequest = useRef(0);
  const unifiedGithubApi = useApi(unifiedGithubApiRef);
  const adoServiceApi = useApi(ADOServiceApiRef);

  const {
    value: adoProjects,
    loading: adoProjectsloading,
    error: adoProjectError,
  } = useAsync(async () => await adoServiceApi.getProjects(), []);

  const {
    value: adoRepos,
    loading: adoReposLoading,
    error: adoRepoError,
  } = useAsync(async () => {
    if (!adoProject) {
      return [];
    }
    return (await adoServiceApi.getRepositories(adoProject.id)) as ADORepo[];
  }, [adoProject]);

  const onSelectAdoProject = (_: any, selectedAdoProject: ADOProject | null, _reason: AutocompleteChangeReason) => {
    if (selectedAdoProject) {
      setAdoProject(selectedAdoProject);
      onChange({ ...formData, adoProject: selectedAdoProject });
    }
  };

  const onSelectAdoRepo = (_: any, adoRepo: ADORepo | null, _reason: AutocompleteChangeReason) => {
    if (adoRepo) {
      setRepos([{ adoRepo: adoRepo.name, githubRepo: adoRepo.name }]);
      onChange({
        ...formData,
        repos: [{ adoRepo: adoRepo.name, githubRepo: adoRepo.name }],
      });
    }
  };

  const [validateRepoNameState, validateRepoName] = useAsyncFn(
    async (newRepo: string) => {
      const requestId = ++lookupRequest.current;
      const repoLookup = await unifiedGithubApi.lookupRepo({ repoName: newRepo });
      if (requestId === lookupRequest.current) {
        setOrganization(repoLookup.owner);
      }
      return validateGithubRepoName(newRepo, 'none', unifiedGithubApi, 'newRepo', repoLookup);
    },
    [unifiedGithubApi],
  );

  const handleGithubRepoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    validateRepoName(value);
    onChange({
      ...formData,
      repos: [{ adoRepo: repos?.[0]?.adoRepo as string, githubRepo: value }],
    });
    setRepos(prevRepos => [{ adoRepo: prevRepos?.[0]?.adoRepo as string, githubRepo: value }]);
  };

  useEffect(() => {
    if (repos?.[0]?.githubRepo) {
      validateRepoName(repos[0].githubRepo);
    }
  }, [repos, validateRepoName]);

  return (
    <Grid container direction="row" spacing={2}>
      <Grid item xs={6}>
        <Autocomplete
          id="ado-project-picker"
          value={adoProject}
          loading={adoProjectsloading}
          onChange={onSelectAdoProject}
          options={adoProjects || []}
          autoSelect
          freeSolo={false}
          style={{ width: '100%' }}
          filterSelectedOptions
          getOptionLabel={(option: any) => option.name}
          renderInput={params => (
            <TextField
              {...params}
              label="ADO Project"
              helperText="Select a ADO Project that contains the repo to migrate to GitHub"
              FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
              variant="outlined"
              required
              InputProps={{ ...params.InputProps, 'aria-label': 'ADO Project' }}
            />
          )}
        />
        {!!adoProjectError && (
          <Alert severity="error" aria-label="ado-project-picker-error" style={{ marginTop: '4px' }}>
            {adoProjectError?.message}
          </Alert>
        )}
      </Grid>
      <Grid item xs={6}>
        <GithubTeamSelect
          formData={formData?.githubTeam ?? ''}
          onChange={(value: any) => {
            onChange({
              ...formData,
              githubTeam: value,
            });
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <Autocomplete
          id="ado-repo-picker"
          value={repos?.[0]?.adoRepo}
          loading={adoReposLoading}
          onChange={onSelectAdoRepo}
          options={adoRepos || []}
          autoSelect
          freeSolo={false}
          style={{ width: '100%' }}
          filterSelectedOptions
          getOptionLabel={(option: any) => option.name}
          renderInput={params => (
            <TextField
              {...params}
              label="ADO Repo"
              helperText="Select a ADO Repo name to migrate to GitHub"
              FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
              variant="outlined"
              required
              InputProps={params.InputProps}
            />
          )}
        />
        {!!adoRepoError && (
          <Alert severity="error" aria-label="ado-repo-picker-error" style={{ marginTop: '4px' }}>
            {adoRepoError?.message}
          </Alert>
        )}
      </Grid>
      <Grid item xs={6}>
        <TextField
          style={{ width: '100%' }}
          variant="outlined"
          label="GitHub Repo Preview"
          inputProps={{
            'aria-label': 'GitHub Repo Preview',
          }}
          helperText="Preview of the GitHub repo name based on ADO repo selection"
          value={repos?.[0]?.githubRepo || ''}
          onChange={handleGithubRepoChange}
          error={!!validateRepoNameState.error}
          InputProps={{ startAdornment: <GithubOrgPrefix organization={organization} /> }}
        />
        {!!validateRepoNameState.error && (
          <Alert severity="error" aria-label="github-repo-error" style={{ marginTop: '4px' }}>
            {validateRepoNameState.error?.message}
          </Alert>
        )}
      </Grid>
    </Grid>
  );
};
